/**
 * MoveHistory Component
 * 
 * Displays chess moves in algebraic notation with move pairs (white and black).
 * Supports scrolling, clicking moves to view positions, and PGN export.
 * Implements requirements 20.1, 20.2, 20.3, 20.4, 20.5, 20.6 from the chess-game specification.
 */

import React, { useRef, useEffect } from 'react';
import { Move, GameSession } from '../types';
import { ChessNotation } from '../ChessNotation';
import './MoveHistory.css';

export interface MoveHistoryProps {
  moves: Move[];
  currentMoveIndex?: number;
  onMoveClick?: (moveIndex: number) => void;
  gameSession?: GameSession;
  className?: string;
}

/**
 * MoveHistory component displays the game's move history in algebraic notation
 * Requirement 20.2: Display move history in scrollable panel
 */
export const MoveHistory: React.FC<MoveHistoryProps> = ({
  moves,
  currentMoveIndex,
  onMoveClick,
  gameSession,
  className = ''
}) => {
  const historyRef = useRef<HTMLDivElement>(null);
  const notation = new ChessNotation();

  // Auto-scroll to the latest move
  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [moves.length]);

  /**
   * Handles clicking on a move to view that position
   * Requirement 20.5: Allow clicking moves to view position
   */
  const handleMoveClick = (index: number) => {
    if (onMoveClick) {
      onMoveClick(index);
    }
  };

  /**
   * Exports the game to PGN format
   * Requirement 20.6: Support PGN export
   */
  const handleExportPGN = () => {
    if (!gameSession) {
      console.warn('Cannot export PGN: No game session available');
      return;
    }

    const pgn = notation.gameToPGN(gameSession, moves);
    
    // Create a blob and download it
    const blob = new Blob([pgn], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chess-game-${Date.now()}.pgn`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Copies PGN to clipboard
   */
  const handleCopyPGN = async () => {
    if (!gameSession) {
      console.warn('Cannot copy PGN: No game session available');
      return;
    }

    const pgn = notation.gameToPGN(gameSession, moves);
    
    try {
      await navigator.clipboard.writeText(pgn);
      // Could add a toast notification here
      console.log('PGN copied to clipboard');
    } catch (err) {
      console.error('Failed to copy PGN:', err);
    }
  };

  /**
   * Groups moves into pairs (white and black)
   * Requirement 20.2: Show move pairs
   */
  const movePairs: Array<{ moveNumber: number; white: Move; black?: Move }> = [];
  
  for (let i = 0; i < moves.length; i += 2) {
    movePairs.push({
      moveNumber: Math.floor(i / 2) + 1,
      white: moves[i],
      black: moves[i + 1]
    });
  }

  return (
    <div className={`move-history ${className}`}>
      <div className="move-history-header">
        <h3>Move History</h3>
        {gameSession && moves.length > 0 && (
          <div className="move-history-actions">
            <button
              className="btn-icon"
              onClick={handleCopyPGN}
              title="Copy PGN to clipboard"
              aria-label="Copy PGN to clipboard"
            >
              📋
            </button>
            <button
              className="btn-icon"
              onClick={handleExportPGN}
              title="Export as PGN file"
              aria-label="Export as PGN file"
            >
              💾
            </button>
          </div>
        )}
      </div>

      {/* Requirement 20.2: Scrollable move history */}
      <div className="move-history-content" ref={historyRef}>
        {moves.length === 0 ? (
          <div className="move-history-empty">
            No moves yet
          </div>
        ) : (
          <>
            {/* Column headers */}
            <div className="move-history-headers">
              <span className="header-number">#</span>
              <span className="header-white">⚪ White</span>
              <span className="header-black">⚫ Black</span>
            </div>
            
            <div className="move-history-list">
              {movePairs.map((pair, pairIndex) => (
                <div key={pairIndex} className="move-pair">
                  <span className="move-number">{pair.moveNumber}.</span>
                  
                  {/* White's move */}
                  <button
                    className={`move-button ${
                      currentMoveIndex === pairIndex * 2 ? 'active' : ''
                    }`}
                    onClick={() => handleMoveClick(pairIndex * 2)}
                    title={`View position after ${pair.white.notation}`}
                  >
                    {/* Requirement 20.3: Use standard notation including symbols */}
                    {pair.white.notation}
                  </button>

                  {/* Black's move (if exists) */}
                  {pair.black && (
                    <button
                      className={`move-button ${
                        currentMoveIndex === pairIndex * 2 + 1 ? 'active' : ''
                      }`}
                      onClick={() => handleMoveClick(pairIndex * 2 + 1)}
                      title={`View position after ${pair.black.notation}`}
                    >
                      {/* Requirement 20.3: Use standard notation including symbols */}
                      {pair.black.notation}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {moves.length > 0 && (
        <div className="move-history-footer">
          <span className="move-count">
            {moves.length} {moves.length === 1 ? 'move' : 'moves'}
          </span>
        </div>
      )}
    </div>
  );
};

export default MoveHistory;
